import {
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { GlobalSearchParameterModel } from './global-search-parameter.model';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { GlobalSearchService } from './global-search.service';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { Subject } from 'rxjs';
import { TransitionOptions } from '@uirouter/angular';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { isNullOrUndefined } from 'util';
import { takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { safeStartsWith } from '@fi-shared/utils/safe-starts-with';
import { UserService } from '@fi-app/user/user.service';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import {
    IGlobalSearchResult,
    IGlobalSearchGroup,
    IGlobalSearchDataViewModel,
} from '@fi-app/components/global-search/global-search';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';

@Component({
    selector: 'fi-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-global-search' },
    standalone: false,
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
    @Output() isMobileSearchOpenedChange = new EventEmitter<boolean>();
    @ViewChild('searchInput') searchInputRef: ElementRef<HTMLInputElement>;

    globalSearchModel = new GlobalSearchParameterModel();

    SearchData: IGlobalSearchDataViewModel[] = [];

    Loading = false;

    SearchValue = '';

    private ModuleItemLimit = 20;
    private SearchPromise: IFiHttpPromise<IGlobalSearchResult[]>;
    private GlobalSearchDelay;
    private globalSearchDelay = 600;
    private tagTexts = ['tag:', 'tagg:'];
    private readonly lightBackgroundColorClassName: string = 'LightBackgroundColor';
    private readonly globalSearchResultItemClassName: string = 'CcmGlobalSearchResultItem';

    constructor(
        private elementRef: ElementRef,
        private objectTypesService: ObjectTypesService,
        private globalSearchService: GlobalSearchService,
        private stateService: FiStateService,
        private stateChangeService: FiStateChangeService,
        private userDataService: FiUserDataService,
        private userService: UserService,
        private renderer: Renderer2,
        private businessUnitService: BusinessUnitService,
    ) {}

    ngOnInit() {
        this.globalSearchModel.ContentLanguageId = this.userDataService.getContentLanguage();
        this.stateChangeService.onFinish$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            if (!this.stateChangedBySelectItem) this.replaceLocationOnSelectItem = false;

            this.stateChangedBySelectItem = false;
        });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    isSimpleView(item: IGlobalSearchResult) {
        return item.ObjectType != ObjectTypes.Post;
    }

    onGlobalSearchTextChange() {
        this.AbortSearchRequest();
        this.Loading = false;
        if (this.GlobalSearchDelay) {
            clearTimeout(this.GlobalSearchDelay);
            this.GlobalSearchDelay = null;
        }

        const existingTagTexts = this.tagTexts.filter((tagText) => {
            return safeStartsWith(this.globalSearchModel.GlobalSearchText, tagText);
        });

        if (existingTagTexts.length) {
            this.globalSearchModel.IsSearchingByTagName = true;
            this.globalSearchModel.SearchText = this.globalSearchModel.GlobalSearchText.substr(
                existingTagTexts[0].length,
            );
        } else {
            this.globalSearchModel.IsSearchingByTagName = false;
            this.globalSearchModel.SearchText = this.globalSearchModel.GlobalSearchText;
        }

        if (this.isNotEmptySearchText()) {
            if (this.globalSearchModel.SearchText != this.SearchValue || this.globalSearchModel.IsSearchingByTagName) {
                this.GlobalSearchDelay = setTimeout(() => {
                    this.GlobalSearchDelay = null;
                    this.doSearch();
                }, this.globalSearchDelay);

                this.globalSearchDelay = 600;
            }
        } else {
            this.SearchData = [];
            this.SearchValue = '';
        }
        this.globalSearchService.raiseShowGlobalSearch(this.IsShowSearchResults());
    }

    doSearch() {
        this.Loading = true;

        // NOTE: https://chainformation.atlassian.net/browse/FI-3081
        // not to change input's value need to create new object and remove spaces from the string beginning and ending before send request
        const editedSearchModel = Object.create(this.globalSearchModel);
        editedSearchModel.GlobalSearchText = editedSearchModel.GlobalSearchText.trim();
        editedSearchModel.SearchText = editedSearchModel.SearchText.trim();
        editedSearchModel.IsSearchingByTagName = this.globalSearchModel.IsSearchingByTagName;
        editedSearchModel.NumberOfRecordsToTake = 100;

        this.SearchValue = this.globalSearchModel.GlobalSearchText;

        this.globalSearchService.raiseShowGlobalSearch(true);

        this.SearchPromise = this.globalSearchService.GetGlobalSearchResults(editedSearchModel);

        this.SearchPromise.then((response) => {
            this.Loading = false;
            const groups: IGlobalSearchGroup = {};
            const data: IGlobalSearchDataViewModel[] = [];
            response.forEach((item) => {
                const groupName: string = this.objectTypesService.GetNameByObjectType(item.ObjectType, true);
                if (groups[groupName] == undefined) {
                    groups[groupName] = {
                        Name: groupName,
                        Data: [],
                        DataShow: [],
                    };
                    data.push(groups[groupName]);
                }
                if (groups[groupName].Data.length < this.ModuleItemLimit) {
                    groups[groupName].Data.push(item);
                } else {
                    groups[groupName].DataShow.push(item);
                }
            });

            this.SearchData = data;
        });
    }

    isUserDeactivated(searchItem: IGlobalSearchResult) {
        if (searchItem.ObjectType === ObjectTypes.User) return !searchItem.IsActivated;

        return false;
    }

    private isNotEmptySearchText() {
        return this.globalSearchModel.SearchText && this.globalSearchModel.SearchText.trim().length > 1;
    }

    IsShowSearchResults() {
        return this.isNotEmptySearchText();
    }

    IsShowNoResults() {
        return this.SearchData.length == 0 && !this.GlobalSearchDelay && this.isNotEmptySearchText();
    }

    CancelSearch() {
        this.AbortSearchRequest();
        this.globalSearchModel.GlobalSearchText = '';
        this.SearchData.length = 0;
        this.onGlobalSearchTextChange();
    }

    AbortSearchRequest() {
        if (this.SearchPromise) this.SearchPromise.cancel();
    }

    private replaceLocationOnSelectItem = false;
    private stateChangedBySelectItem = false;

    private goToStateOnSelectItem(to: string, params) {
        const options: TransitionOptions = this.replaceLocationOnSelectItem
            ? {
                  location: 'replace',
                  reload: true,
              }
            : undefined;
        this.stateService.go(to, params, options);
        this.replaceLocationOnSelectItem = true;
        this.stateChangedBySelectItem = true;
    }

    onItemSelect(item: IGlobalSearchResult) {
        if (!isDefined(item)) return;

        if (item.ObjectType != ObjectTypes.Tag) this.CloseMobileSearch();

        if (item.ObjectType == ObjectTypes.MediaBank) {
            this.goToStateOnSelectItem(FiStateNames.MediaBank.View, { Id: item.ObjectId });
        } else if (item.ObjectType == ObjectTypes.Document) {
            this.goToStateOnSelectItem(FiStateNames.Document.View, {
                Id: item.ObjectId,
                highlightText: this.globalSearchModel.GlobalSearchText,
            });
        } else if (item.ObjectType == ObjectTypes.BusinessUnit) {
            this.businessUnitService.openBusinessUnitViewDialog(item.ObjectId);
        } else if (item.ObjectType == ObjectTypes.User) {
            this.userService.openUserViewDialog(item.ObjectId);
        } else if (item.ObjectType == ObjectTypes.UserAccessRequest) {
            this.goToStateOnSelectItem(FiStateNames.User.Edit, { Id: item.ObjectId });
        } else if (item.ObjectType === ObjectTypes.Post || item.ObjectType === ObjectTypes.ForumPost) {
            this.goToStateOnSelectItem(FiStateNames.PostView, {
                objectId: item.ObjectId,
                objectType: item.ObjectType,
                forumId: item.ForumId,
                highlightText: this.globalSearchModel.GlobalSearchText,
            });
        } else if (item.ObjectType == ObjectTypes.Tag) {
            this.globalSearchDelay = 0;
            this.globalSearchModel.GlobalSearchText = this.tagTexts[0] + item.Name;
            this.onGlobalSearchTextChange();
        }
    }

    ShowMore(group, $event) {
        $event.preventDefault();
        group.Data = group.Data.concat(group.DataShow.splice(0, this.ModuleItemLimit));
    }

    IsShowMore(group) {
        return group.DataShow.length > 0;
    }

    // keyb oard navigation
    InputKeydown(event) {
        if ([27, 38, 40].indexOf(event.keyCode) == -1) event.stopPropagation();
    }

    resetSelectedElement(element: HTMLElement): void {
        const currentSelectedElement: HTMLElement = this.getCurrentSelectedElement();
        if (isNullOrUndefined(currentSelectedElement)) {
            this.renderer.addClass(element, this.lightBackgroundColorClassName);
            return;
        }
        this.renderer.removeClass(currentSelectedElement, this.lightBackgroundColorClassName);
        this.renderer.addClass(element, this.lightBackgroundColorClassName);
    }

    private getCurrentSelectedElement(): HTMLElement {
        return this.elementRef.nativeElement.querySelector('.' + this.lightBackgroundColorClassName);
    }

    goToSearchItem(): void {
        const currentSelectedElement = this.getCurrentSelectedElement();
        if (currentSelectedElement) {
            currentSelectedElement.click();
        }
    }

    keyboardIndexChange(newKeyboardIndex): void {
        const currentSelectedElement: HTMLElement = this.getCurrentSelectedElement();
        if (!isNullOrUndefined(currentSelectedElement))
            this.renderer.removeClass(currentSelectedElement, this.lightBackgroundColorClassName);

        if (newKeyboardIndex > -1)
            this.renderer.addClass(this.GetAllSearchElements()[newKeyboardIndex], this.lightBackgroundColorClassName);

        this.scrollToSelectedElement();
    }

    getKeyboardIndexMax(): number {
        return this.GetAllSearchElements().length - 1;
    }

    private scrollToSelectedElement(): void {
        const currentSelectedElement: HTMLElement = this.getCurrentSelectedElement();
        if (isNullOrUndefined(currentSelectedElement)) return;

        currentSelectedElement.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
        });
    }

    private GetAllSearchElements(): HTMLElement[] {
        return Array.from(this.elementRef.nativeElement.querySelectorAll('.' + this.globalSearchResultItemClassName));
    }

    OpenMobileSearch() {
        this.isMobileSearchOpenedChange.emit(true);
    }

    CloseMobileSearch() {
        this.isMobileSearchOpenedChange.emit(false);
    }

    getSafeHtml(str: string) {
        return fiTextToHtml(str);
    }
}
